import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { ReactNode } from 'react';
import { Trans } from 'react-i18next';

import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';

const EXAMPLE_TOTAL = 1000;
const EXAMPLE_INSTALLMENTS = 10;
const EXAMPLE_PER_SETTLEMENT = EXAMPLE_TOTAL / EXAMPLE_INSTALLMENTS;

const tooltipSlotProps = {
  tooltip: {
    sx: (theme: Theme) => ({
      backgroundColor: theme.semantic.surfaceRaised,
      border: `1px solid ${theme.brandV2.colors.treadGray7}`,
      borderRadius: 1.5,
      boxShadow: 3,
      color: theme.brandV2.colors.treadGray2,
      maxWidth: 420,
      p: 1.5,
    }),
  },
};

const headerCellSx = {
  fontSize: 11,
  fontWeight: 600,
  pb: 0.5,
  textAlign: 'right',
} as const;

const bodyCellSx = {
  borderTop: '1px solid',
  borderTopColor: 'divider',
  fontSize: 12,
  py: 0.5,
  textAlign: 'right',
} as const;

const helpTextSx = (theme: Theme) => ({ color: theme.brandV2.colors.treadGray2 });
const sectionLabelSx = (theme: Theme) => ({
  color: theme.brandV2.colors.treadGray3,
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  mb: 0.5,
  textTransform: 'uppercase' as const,
});
const leadInSx = (theme: Theme) => ({
  color: theme.brandV2.colors.treadGray1,
  fontWeight: 700,
});
const helpTermSx = (theme: Theme) => ({
  color: theme.brandV2.colors.treadGray1,
  fontWeight: 700,
});

interface AddOnHelpLinkProps {
  children: ReactNode;
  label: string;
  testId: string;
}

const AddOnHelpLink = ({ children, label, testId }: AddOnHelpLinkProps) => (
  <Tooltip placement="top-start" slotProps={tooltipSlotProps} title={children}>
    <Link
      component="button"
      data-test-id={testId}
      type="button"
      underline="hover"
      variant="body2"
      sx={{
        // MuiLink's default primary is the brand yellow, which is unreadable as
        // text on the dialog's white surface.
        color: 'info.main',
        fontWeight: 600,
      }}
    >
      {label}
    </Link>
  </Tooltip>
);

const RatesHelpContent = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    <Box>
      <Typography variant="body2" sx={helpTermSx}>
        {t('administration.rates.invoice_rate')}
      </Typography>
      <Typography variant="body2" sx={helpTextSx}>
        {t('administration.rates.automatic_add_ons.invoice_rate_help')}
      </Typography>
    </Box>
    <Box>
      <Typography variant="body2" sx={helpTermSx}>
        {t('administration.rates.add_ons.cost_rate')}
      </Typography>
      <Typography variant="body2" sx={helpTextSx}>
        {t('administration.rates.automatic_add_ons.cost_rate_help')}
      </Typography>
    </Box>
  </Box>
);

const InstallmentsHelpContent = () => {
  const { currencyFormatter } = useCompanyCurrency();

  const exampleRows = [
    {
      amount: currencyFormatter(EXAMPLE_TOTAL),
      label: t('administration.rates.automatic_add_ons.installments_help_charge'),
      perSettlement: currencyFormatter(EXAMPLE_PER_SETTLEMENT),
    },
    {
      amount: currencyFormatter(-EXAMPLE_TOTAL),
      label: t('administration.rates.automatic_add_ons.installments_help_deduction'),
      perSettlement: currencyFormatter(-EXAMPLE_PER_SETTLEMENT),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Typography variant="body2" sx={helpTextSx}>
        <Trans
          components={{ lead: <Box component="span" sx={leadInSx} /> }}
          i18nKey="administration.rates.automatic_add_ons.installments_help_blank"
        />
      </Typography>
      <Typography variant="body2" sx={helpTextSx}>
        <Trans
          components={{ lead: <Box component="span" sx={leadInSx} /> }}
          i18nKey="administration.rates.automatic_add_ons.installments_help_number"
        />
      </Typography>

      <Box
        sx={(theme) => ({
          backgroundColor: theme.brandV2.colors.treadGray9,
          border: `1px solid ${theme.brandV2.colors.treadGray7}`,
          borderRadius: 1,
          p: 1.25,
        })}
      >
        <Typography variant="body2" sx={sectionLabelSx}>
          {t('administration.rates.automatic_add_ons.installments_help_examples_label')}
        </Typography>
        <Typography variant="body2" sx={helpTextSx}>
          {t('administration.rates.automatic_add_ons.installments_help_examples_intro', {
            installments: EXAMPLE_INSTALLMENTS,
            total: currencyFormatter(EXAMPLE_TOTAL),
          })}
        </Typography>

        <Box
          component="table"
          sx={(theme) => ({
            borderCollapse: 'collapse',
            color: theme.brandV2.colors.treadGray1,
            mt: 1,
            width: '100%',
          })}
        >
          <Box component="thead">
            <Box component="tr">
              <Box
                component="th"
                scope="col"
                sx={{ ...headerCellSx, textAlign: 'left' }}
              />
              <Box component="th" scope="col" sx={headerCellSx}>
                {t('administration.rates.invoice_rate')}
              </Box>
              <Box component="th" scope="col" sx={headerCellSx}>
                {t('administration.rates.automatic_add_ons.total_installments')}
              </Box>
              <Box component="th" scope="col" sx={headerCellSx}>
                {t(
                  'administration.rates.automatic_add_ons.installments_help_each_settlement',
                )}
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {exampleRows.map((row) => (
              <Box component="tr" key={row.label}>
                <Box
                  component="th"
                  scope="row"
                  sx={{ ...bodyCellSx, fontWeight: 500, textAlign: 'left' }}
                >
                  {row.label}
                </Box>
                <Box component="td" sx={bodyCellSx}>
                  {row.amount}
                </Box>
                <Box component="td" sx={bodyCellSx}>
                  {EXAMPLE_INSTALLMENTS}
                </Box>
                <Box component="td" sx={{ ...bodyCellSx, fontWeight: 700 }}>
                  {row.perSettlement}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Typography variant="body2" sx={helpTextSx}>
        {t('administration.rates.automatic_add_ons.installments_help_quantity_note')}
      </Typography>
    </Box>
  );
};

const AddOnHelpLinks = () => (
  <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
    <InfoOutlined sx={{ color: 'info.main', fontSize: 15 }} />
    <AddOnHelpLink
      label={t('administration.rates.automatic_add_ons.rates_help_link')}
      testId="add-on-rates-help-link"
    >
      <RatesHelpContent />
    </AddOnHelpLink>
    <Box
      aria-hidden
      component="span"
      sx={(theme) => ({ color: theme.brandV2.colors.treadGray4 })}
    >
      &bull;
    </Box>
    <AddOnHelpLink
      label={t('administration.rates.automatic_add_ons.installments_help_link')}
      testId="add-on-installments-help-link"
    >
      <InstallmentsHelpContent />
    </AddOnHelpLink>
  </Box>
);

export default AddOnHelpLinks;
